<template>
  <div class="search-result-detail-wrapper">
    <!-- 검색 결과, 활성화된 메뉴에 따라 resultData 가 변경된다. -->
    <div v-for="item in resultData" :key="item.label">
      <div v-if="resultData.length !== 1 && item.count !== 0" class="header">{{ item.menuName }} ({{ item.count }})</div>
      <!-- 중분류 -->
      <div class="result-box border" v-for="typeItem in item.data" :key="`${item.menuId}.${typeItem.type}`" v-loading="loading[getSearchResultTypeKey(item.menuId, typeItem.type)]">
        <div class="title">{{ typeItem.label }} ({{ typeItem.count }})</div>
        <div>
          <!-- 소분류 -->
          <ul class="list">
            <li class="item" v-for="(result, idx) in typeItem.data" :key="`${item.menuId}.${typeItem.type}.${idx}`">
              <span v-html="getSearchResultLabel(item.menuId, typeItem.type, result)" @click="actionByResultMenu(result, item.menuId, typeItem.type)"></span>
              <el-dropdown v-if="item.menuId === MENU.PERF_OPER" trigger="click" placement="bottom-start" @command="handleTabDropdownCommand">
                <i class="link-button far fa-external-link"></i>
                <!-- 성능운영 > 관리대상 -->
                <el-dropdown-menu v-if="typeItem.type === SEARCH_TARGET.OBJ" slot="dropdown">
                  <el-dropdown-item v-for="tab in getValidPerformanceNodeTabs(result)" :key="tab.name" :command="{ result, menuId: item.menuId, type: typeItem.type, tabId: tab.name }">
                    {{ tab.label }}
                  </el-dropdown-item>
                </el-dropdown-menu>
                <!-- 성능운영 > 관리그룹 -->
                <el-dropdown-menu v-else-if="typeItem.type === SEARCH_TARGET.LOGICAL_GROUP" slot="dropdown">
                  <el-dropdown-item v-for="tab in getValidPerformanceGroupTabs(result)" :key="tab.name" :command="{ result, menuId: item.menuId, type: typeItem.type, tabId: tab.name }">
                    {{ tab.label }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <!-- 이벤트 현황 -->
              <i v-if="!isEmpty(result.items)" :class="['expand-button', 'fa', result.expanded ? 'fa-chevron-circle-up' : 'fa-chevron-circle-down']" @click="result.expanded = !result.expanded"></i>
              <div class="sub-result-box" v-if="!isEmpty(result.items)" v-show="result.expanded">
                <ul>
                  <li v-for="(evt, index) in result.items" :key="`${evt.hashkeyIdString}.${index}`">
                    <div class="eventlevel-tag" :style="getEventlevelStyle(evt.eventlevelId)">
                      {{ getEventlevelName(evt.eventlevelId) }}
                    </div>
                    <span class="link" style="margin-left:5px;" @click="actionByResultEvent(evt)">
                      {{ evt.eventMessage }}
                    </span>
                    <span class="datetime"> ({{ getDateToString(evt.checkDatetime) }}) </span>
                  </li>
                </ul>
                <div class="more" v-if="result.eventCount > 10" @click="actionByResultMenu(result, item.menuId, typeItem.type)">이벤트 현황 전체보기 <i class="fas fa-angle-right" /></div>
              </div>
            </li>
          </ul>
          <div class="more" style="margin:15px 0 10px 10px; " v-if="isTotalView && (typeItem.count > DEFAULT_PAGE_SIZE)">
            <span @click="menuChange(item.menuId)"> [{{ item.menuName }} {{ typeItem.label }}] 검색 결과 더보기 <i class="fas fa-angle-right" /> </span>
          </div>
        </div>
        <div class="pagination-bar" v-if="!isTotalView">
          <el-row type="flex" justify="center">
            <w-pagination ref="pagination" :total="typeItem.count" :params="{ menuId: item.menuId, type: typeItem.type, ...typeItem.params }" @condition-change="loadData" />
          </el-row>
        </div>
      </div>
    </div>
    <!-- 검색 결과 없음 -->
    <div v-if="isEmptyData" class="no-data">
      <img src="@/assets/img/no_result_found.gif" />
      <div class="main-text">
        <span class="keyword">'{{ searchKeyword }}'</span>에 대한 검색 결과가 없습니다.
      </div>
      <ul class="info">
        <li>검색어를 올바르게 입력했는지 확인해주세요.</li>
        <li>검색어가 두 단어 이상인 경우, 정확하게 띄어쓰기를 한 후 검색해보세요.</li>
        <li>권한이 없는 정보는 검색 결과로 제공되지 않습니다.</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEmpty, isNil, find, findIndex, cloneDeep, forEach } from 'lodash';
import { openFaultAnalysisPopup } from '@/utils/event';
import { defaultPagination } from '@/constants/table';
import DashboardConst from '@/constants/dashboard';
import { menuDefine } from '@/constants/menu';
import { performanceNodeTabs, performanceGroupTabs } from '@/constants/performance';
import msgUtil from '@/utils/message';
import { getDateToString } from '@/utils/date';
import { isTrue } from '@/utils/boolean';
import { routePathByObjId, routePath } from '@/utils/router';
import smartSearchApi from '@/api/frame/smart-search';
import { highlightKeyword } from '../utils/search';
import WPagination from './components/pagination';

const MENU = {
  DASHBOARD: 'DASHBOARD',
  TOPOLOGYMAP: 'TOPOLOGYMAP',
  PERF_OPER: 'PERF_OPER',
  CHART: 'CHART',
  LOG: 'LOG',
  LOG_ANALYSIS: 'LOG_ANALYSIS',
  EVENT: 'EVENT',
  WORK: 'WORK',
  REPORT: 'REPORT',
};
const SEARCH_TARGET = {
  OBJ: 'OBJ',
  LOGICAL_GROUP: 'LOGICAL_GROUP',
  DASHBOARD: 'DASHBOARD',
  TOPOLOGYMAP: 'TOPOLOGYMAP',
  CHART_TEMPLATE: 'CHART_TEMPLATE',
  LOG_FILTER: 'LOG_FILTER',
  WORK: 'WORK',
  REPORT: 'REPORT',
};

const DEFAULT_PAGE_SIZE = defaultPagination.pageSize;

export default {
  name: 'WSearchResultDetail',
  components: {
    WPagination,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * 전체 탭 여부
     */
    isTotalView: {
      type: Boolean,
    },
    /**
     * 검색 키워드
     */
    searchKeyword: {
      type: String,
    },
  },
  data() {
    return {
      defaultPagination: cloneDeep(defaultPagination),
      resultData: [],
      loading: {},
      DEFAULT_PAGE_SIZE,
      MENU,
      SEARCH_TARGET,
    };
  },
  computed: {
    isLms() {
      return findIndex(this.activeManagetypeIds, (item) => item === 'LMS') >= 0;
    },
    isEmptyData() {
      return isEmpty(this.data) || this.data === [];
    },
    ...mapGetters(['activeManagetypeIds', 'roles', 'eventlevelInfo']),
  },
  watch: {
    data: {
      immediate: true,
      handler(value) {
        if (this.isTotalView) {
          this.loading = {};
        } else {
          const loading = {};
          forEach(value, (item) => {
            forEach(item.data, (type) => {
              loading[this.getSearchResultTypeKey(item.menuId, type.type)] = false;
            });
          });
          this.loading = loading;
        }

        if (isEmpty(value) || value.length > 1) {
          this.resultData = value;
        } else {
          this.resultData = cloneDeep(value);
        }
      },
    },
  },
  methods: {
    isNil,
    isEmpty,
    getDateToString,
    /**
     * 조건에 맞는 검색 결과를 조회한다.
     * @author Ju
     * @param  {[type]} params [description]
     * @return {[type]}        [description]
     */
    loadData(params) {
      const action = msgUtil.action.VIEW;
      const target = '검색 결과';
      const dataKey = this.getSearchResultTypeKey(params.menuId, params.type);
      this.loading[dataKey] = true;
      smartSearchApi
        .getSearchResultByMenuType(params)
        .then((response) => {
          if (response.data.success) {
            const menu = find(this.resultData, (item) => item.menuId === params.menuId);
            const type = find(menu.data, (item) => item.type === params.type);
            type.data = cloneDeep(response.data.data);
          } else {
            this.$message(msgUtil.getResultMsgData(action, target, false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
          throw new Error(e);
        })
        .finally(() => {
          this.loading[dataKey] = false;
        });
    },
    /**
     * 검색 결과 정보 타입 별 키를 반환한다.
     * @author Ju
     * @param  {[type]} menuId [description]
     * @param  {[type]} type   [description]
     * @return {[type]}        [description]
     */
    getSearchResultTypeKey(menuId, type) {
      return `${menuId}.${type}`;
    },
    /**
     * 성능/운영 노드 별 탭 리스트 반환
     * @author jeee12
     * @param  {[type]} obj [description]
     * @return {[type]}     [description]
     */
    getValidPerformanceNodeTabs(obj) {
      const roles = this.roles;
      const nodeTabList = Object.keys(performanceNodeTabs).reduce((array, key) => {
        const tab = performanceNodeTabs[key];
        if (!tab.validate) {
          array.push(tab);
        } else if (tab.validate && isTrue(tab.validate(obj, roles))) {
          array.push(tab);
        }
        return array;
      }, []);

      return nodeTabList.sort((a, b) => a.orderbyIndex - b.orderbyIndex);
    },
    /**
     * 성능/운영 노드 별 탭 리스트 반환
     * @author jeee12
     * @return {[type]}     [description]
     */
    getValidPerformanceGroupTabs() {
      const groupTabList = Object.keys(performanceGroupTabs).map((key) => performanceGroupTabs[key]);
      return groupTabList.sort((a, b) => a.orderbyIndex - b.orderbyIndex);
    },
    /**
     * 검색 결과 소분류에 나타낼 라벨을 반환한다.
     * @author Hunui
     * @param  {[type]} item [description]
     * @param  {[type]} type [description]
     * @param  {[type]} item [description]
     * @param  {[type]} tab  (성능/운영 메뉴의 경우, 탭 별로 표현하기 위해 필요)
     * @return {[type]}      [description]
     */
    getSearchResultLabel(menu, type, item, tab) {
      let result = '';
      const separator = '<i class="far fa-angle-right separator"></i>';
      if (type === SEARCH_TARGET.OBJ) {
        let highlightTarget = '';
        // 공통 label
        result += `${this.getSearchResultLabelTypeTag(item.objDefineName)}`;
        highlightTarget = `${item.objName}`;
        if (!isNil(item.ipaddress)) {
          highlightTarget += ` (${item.ipaddress})`;
        }
        result = `${result} ${highlightKeyword(highlightTarget, this.searchKeyword)}`;

        if (menu === MENU.PERF_OPER && !isNil(tab)) {
          result += `${separator}${this.getSearchResultLastLabelTag(tab.label)}`;
          item.tabId = tab.name;
        } else if (menu === MENU.TOPOLOGYMAP) {
          result += `${separator}${this.getSearchResultLastLabelTag(item.mapName)}`;
        } else if (menu === MENU.CHART) {
          result += `${separator}${item.templateName}`;
        } else if (menu === MENU.EVENT) {
          result += ` (${item.eventCount})`;
        } else if (menu === MENU.WORK) {
          result += `${separator}${this.getSearchResultLastLabelTag(item.workName)}${this.getWorkSearchResultDateLabelTag(item)}`;
        } else if (menu === MENU.REPORT) {
          result += `${separator}${item.reportName}`;
        }
      } else if (type === SEARCH_TARGET.LOGICAL_GROUP) {
        result += `${highlightKeyword(item.managegroupName, this.searchKeyword)}`;
        if (menu === MENU.PERF_OPER && !isNil(tab)) {
          result += `${separator}${tab.label}`;
          item.tabId = tab.name;
        }
      } else if (type === SEARCH_TARGET.DASHBOARD) {
        result += `${this.getSearchResultLabelTypeTag(item.dashboardType === DashboardConst.DASHBOARD_TYPE.COORD ? '커스텀' : '기본')}대시보드${separator}`;
        result += `${highlightKeyword(item.dashboardName, this.searchKeyword)}`;
      } else if (type === SEARCH_TARGET.TOPOLOGYMAP) {
        result += `${highlightKeyword(item.mapName, this.searchKeyword)}`;
      } else if (type === SEARCH_TARGET.CHART_TEMPLATE) {
        result += `${highlightKeyword(item.templateName, this.searchKeyword)}`;
      } else if (type === SEARCH_TARGET.LOG_FILTER) {
        if (this.isLms) {
          result += `${highlightKeyword(item.label, this.searchKeyword)}`;
        } else if (item.type === 'USER') {
          // 사용자 정의 필터
          // result += `${this.getSearchResultLabelTypeTag('사용자 정의')}${item.filterName}`;
          result += `${highlightKeyword(item.filterName, this.searchKeyword)}`;
        }
        // 20.01.15 Ju NOTE: 기본 로그 이벤트 로그 필터 기획/설계 수정 필요하여 이벤트 로그 필터는 수정 될 때까지 미제공.
        // else if (item.type === 'EVENT') { // 이벤트 로그 필터
        //   result += `${this.getSearchResultLabelTypeTag('이벤트 로그')}${item.filterId}${separator}${item.objName}`;
        // }
      } else if (type === SEARCH_TARGET.WORK) {
        result += `${highlightKeyword(item.workName, this.searchKeyword)}${this.getWorkSearchResultDateLabelTag(item)}`;
      } else if (type === SEARCH_TARGET.REPORT) {
        result += `${highlightKeyword(item.reportName, this.searchKeyword)}`;
      }
      result = `<span class="link">${result}</span>`;
      return result;
    },
    getWorkSearchResultDateLabelTag(result) {
      return `<span class="datetime">(${isNil(result.scheduleSeqNumber) ? `${this.getDateToString(result.startDatetime)} ~ ${this.getDateToString(result.endDatetime)}` : `스케쥴: ${result.scheduleDetail}}`})</span>`;
    },
    /**
     * 검색 결과 라벨의 타입에 대한 태그를 반환한다.
     * @author Ju
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    getSearchResultLabelTypeTag(value) {
      return `<span class="type">${value}</span>`;
    },
    /**
     * 검색 결과 라벨의 타입에 대한 태그를 반환한다.
     * @author Ju
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    getSearchResultLastLabelTag(value) {
      return `<span class="last-label">${value}</span>`;
    },
    /**
     * 메뉴 탭 변경 핸들러
     * @author Hunui
     * @param  {[type]} menu [description]
     * @return {[type]}      [description]
     */
    menuChange(menu) {
      this.$emit('menu-change', menu);
    },
    /**
     * 검색 결과항목 클릭 시 타입에 따른 처리를 실행한다.
     * @author Hunui
     * @param  {[type]} result [description]
     * @param  {[type]} type   [description]
     * @return {[type]}        [description]
     */
    actionByResultMenu(result, menu, type) {
      switch (menu) {
        case MENU.DASHBOARD:
          this.routePathByTreeKeyData(result, menu);
          break;
        case MENU.TOPOLOGYMAP:
          this.routePathByTreeKeyData(result, menu);
          break;
        case MENU.PERF_OPER:
          if (type === SEARCH_TARGET.OBJ) {
            routePathByObjId(this, result.objId, menuDefine[MENU.PERF_OPER], result.tabId, null, null, true);
          } else if (type === SEARCH_TARGET.LOGICAL_GROUP) {
            this.routePathByTreeKeyData(result, menu, type);
          }
          break;
        case MENU.CHART:
          routePath(this, menuDefine[MENU.CHART], null, null, true, {
            chartTemplateSeq: result.templateSeq,
          });
          break;
        case MENU.LOG_ANALYSIS:
        case MENU.LOG:
          if (type === SEARCH_TARGET.OBJ) {
            this.$emit('open-log-popup', result);
          } else if (type === SEARCH_TARGET.LOG_FILTER) {
            this.routePathByTreeKeyData(result, menu);
          }
          break;
        case MENU.EVENT:
          routePathByObjId(this, result.objId, menuDefine[MENU.EVENT], null, null, null, true);
          break;
        case MENU.WORK:
          this.$store.dispatch('openWorkDetailsSlidingPopup', {
            viewTypeTimeLine: true,
            ...result,
          });
          break;
        case 'REPORT':
          this.routePathByTreeKeyData(result, menu);
          break;
        default:
          break;
      }
    },
    /**
     * 선택한 검색 결과를 기준으로 트리키를 생성하고, 해당 경로로 새로운 탭을 제공한다.
     * @author jeee12
     * @param  {[type]} params [description]
     * @param  {[type]} menuId [description]
     */
    routePathByTreeKeyData(params, menuId, type) {
      const action = msgUtil.action.VIEW;
      const target = '메뉴 경로';
      smartSearchApi
        .getTreeKey({
          menuId,
          ...params,
        })
        .then((response) => {
          if (response.data.success) {
            const treeKey = response.data.data;
            if (menuId === MENU.LOG_ANALYSIS && this.isLms) {
              routePath(this, menuDefine.LOG_ANALYSIS, treeKey, null, true);
            } else if (menuId === MENU.PERF_OPER && type === SEARCH_TARGET.LOGICAL_GROUP) {
              routePath(this, menuDefine[menuId], treeKey, params.tabId, true);
            } else if (menuId === MENU.TOPOLOGYMAP) {
              routePath(this, menuDefine[menuId], treeKey, params.objId, true);
            } else {
              routePath(this, menuDefine[menuId], treeKey, null, true);
            }
          } else {
            this.$message(msgUtil.getResultMsgData(action, target, false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
          throw new Error(e);
        });
    },
    /**
     * 장애이벤트 클릭 시 팝업 오픈
     * @author Hunui
     * @param  {[type]} evt [description]
     * @return {[type]}     [description]
     */
    actionByResultEvent(evt) {
      openFaultAnalysisPopup(evt.eventSeqNumber);
    },
    /**
     * 이벤트 등급 아이디에 맞는 이벤트 등급 스타일을 반환한다.
     * @author Ju
     * @param  {[type]} eventlevelId [description]
     * @return {[type]}              [description]
     */
    getEventlevelStyle(eventlevelId) {
      if (!isEmpty(this.eventlevelInfo) && !isEmpty(this.eventlevelInfo[eventlevelId])) {
        const eventlevel = this.eventlevelInfo[eventlevelId];
        return {
          background: eventlevel.eventlevelColorBg,
          color: eventlevel.eventlevelColorText,
        };
      }
      return {
        background: '#fff',
        color: '#000',
      };
    },
    /**
     * 이벤트 등급 아이디에 맞는 이벤트 등급명을 반환한다.
     * @author Ju
     * @param  {[type]} eventlevelId [description]
     * @return {[type]}              [description]
     */
    getEventlevelName(eventlevelId) {
      if (!isEmpty(this.eventlevelInfo) && !isEmpty(this.eventlevelInfo[eventlevelId])) {
        return this.eventlevelInfo[eventlevelId].eventlevelName;
      }
      return eventlevelId;
    },
    /**
     * 성능운영 > 관리대상 / 관리그룹의 탭 목록 dropdown item 클릭
     * @author jeee12
     * @param  {[type]} command [description]
     * @return {[type]}         [description]
     */
    handleTabDropdownCommand(command) {
      const result = { tabId: command.tabId, ...command.result };
      this.actionByResultMenu(result, command.menuId, command.type);
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/views/frame/components/popup/SmartSearchPopup/SearchResultView/SearchResultDetail.vue